import {useMenuItem} from '@react-aria/menu';
import {useHover} from '@react-aria/interactions';
import type {Item} from '@react-stately/collections';
import type {Key, Node} from '@react-types/shared';
import type {TreeState} from '@react-stately/tree';
import React from 'react';
import cx from 'classnames';

import {ThemeConsumer} from '../../../themes/ThemeContext';
import {Divider} from '../../elements/Divider/Divider';
import {Icon, IconTypes} from '../../elements/Icon/Icon';
import type {AriaMenuItem} from './AriaMenu.types';

import styles from './AriaMenu.module.scss';

export const InnerAriaMenuItem: React.FC<{
    item: Node<typeof Item>;
    state: TreeState<typeof Item>;
    itemMap: Map<Key, AriaMenuItem>;
    itemClassName?: string;
    showSubMenuIndicator?: boolean;
    restoreFocus?: boolean;
    onFocusRestored?: () => void;
}> = ({
    item,
    state,
    itemMap,
    itemClassName,
    showSubMenuIndicator = true,
    restoreFocus,
    onFocusRestored,
}) => {
    const ref = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (restoreFocus && ref.current) {
            ref.current.focus();
            onFocusRestored?.();
        }
    }, [onFocusRestored, restoreFocus]);

    const {isHovered, hoverProps} = useHover({
        onHoverStart: () => state.selectionManager.setFocusedKey(item.key),
        onHoverEnd: () => {
            if (state.selectionManager.focusedKey === item.key) {
                state.selectionManager.setFocusedKey(null);
            }
        },
    });

    const {menuItemProps} = useMenuItem(
        {
            key: item.key,
        },
        state,
        ref,
    );

    // Prevent default on pointer up to prevent unexpected menu item selection when the menu is opened by mouse click
    menuItemProps.onPointerUp = e => {
        e.preventDefault();
    };

    // Handle focus events so we can apply highlighted
    const isFocused = state.selectionManager.focusedKey === item.key;

    const alternativeHoverClassName = itemMap.get(
        item.key,
    )?.alternativeHoverClassName;

    const alternativeHoverStyle = itemMap.get(item.key)?.alternativeHoverStyle;

    const itemIcon = itemMap.get(item.key)?.icon;
    const iconPosition = itemMap.get(item.key)?.iconPosition ?? 'start';
    const hasEndIcon = iconPosition === 'end' && Boolean(itemIcon);
    const hasSubMenu = Boolean(itemMap.get(item.key)?.subMenu);

    // Highlight if focused or hovered but not both to allow keyboard navigation even if mouse is over the item
    const shouldHighlight = isFocused || (isFocused && !isHovered);

    const isChecked = itemMap.get(item.key)?.isChecked;

    const checkableProps =
        isChecked === undefined
            ? {}
            : {role: 'menuitemcheckbox' as const, 'aria-checked': isChecked};

    return (
        <ThemeConsumer>
            {({colorScheme}) => (
                <>
                    {itemMap.get(item.key)?.withTopDivider && (
                        <Divider role="separator" className={styles.divider} />
                    )}
                    <div className={styles.innerMenuItemWrapper}>
                        <div
                            {...menuItemProps}
                            {...hoverProps}
                            aria-haspopup={hasSubMenu ? 'menu' : undefined}
                            data-testid={itemMap.get(item.key)?.testId}
                            ref={ref}
                            style={{
                                ...itemMap.get(item.key)?.style,
                                ...(shouldHighlight
                                    ? alternativeHoverStyle
                                    : {}),
                            }}
                            className={cx(
                                styles[colorScheme],
                                styles.innerMenuItem,
                                shouldHighlight
                                    ? (alternativeHoverClassName ??
                                          styles.highlighted)
                                    : {},
                                itemMap.get(item.key)?.className,
                                itemClassName,
                                {
                                    [styles.isDisabled]: itemMap.get(item.key)
                                        ?.isDisabled,
                                    [styles.isDanger]: itemMap.get(item.key)
                                        ?.isDanger,
                                },
                            )}
                            data-active={itemMap.get(item.key)?.isActive}
                            {...checkableProps}
                        >
                            {iconPosition === 'start' && itemIcon}
                            {item.rendered}
                            {iconPosition === 'end' && itemIcon && (
                                <span className={styles.innerMenuItemEndIcon}>
                                    {itemIcon}
                                </span>
                            )}
                            {hasSubMenu && showSubMenuIndicator && (
                                <Icon
                                    source={IconTypes.IconChevronRight}
                                    size="compact"
                                    className={cx(
                                        styles.innerMenuSubMenuIndicator,
                                        {
                                            [styles.innerMenuSubMenuIndicatorPushToEnd]:
                                                !hasEndIcon,
                                        },
                                    )}
                                />
                            )}
                        </div>
                        {itemMap.get(item.key)?.description && (
                            <div>{itemMap.get(item.key)?.description}</div>
                        )}
                    </div>
                    {itemMap.get(item.key)?.withBottomDivider && (
                        <Divider role="separator" className={styles.divider} />
                    )}
                </>
            )}
        </ThemeConsumer>
    );
};
